import { useEffect, useRef } from "react";

interface UseBeforeUnloadWarningProps {
  isRecording: boolean;
  isPaused: boolean;
  activeUploads: Set<string>;
  uploadInitializing: Set<string>;
  pendingChunks: Map<string, Array<{ chunkNo: number; size: number }>>;
}

/**
 * Custom hook to show a warning when user tries to reload/close tab
 * while recording is in progress or chunks are uploading
 */
export const useBeforeUnloadWarning = ({
  isRecording,
  isPaused,
  activeUploads,
  uploadInitializing,
  pendingChunks,
}: UseBeforeUnloadWarningProps) => {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Check if recording is active (not paused)
      const isRecordingActive = isRecording && !isPaused;

      // Check if there are active uploads or pending chunks

      const hasUploadsInProgress =
        activeUploads.size > 0 ||
        uploadInitializing.size > 0 ||
        pendingChunks.size > 0;

      if (isRecordingActive || hasUploadsInProgress) {
        // Prevent the default action
        event.preventDefault();

        // Set a return value for older browsers
        event.returnValue = "";
        // Return a message for modern browsers
        if (isRecordingActive && hasUploadsInProgress) {
          return "Recording is in progress and files are being uploaded. Are you sure you want to leave?";
        } else if (isRecordingActive) {
          return "Recording is in progress. Are you sure you want to leave?";
        } else if (hasUploadsInProgress) {
          return "Files are being uploaded. Are you sure you want to leave?";
        }
      }
    };

    // Add the event listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isRecording, isPaused]);

  return null; // This hook doesn't render anything
};
